import styled from 'styled-components';
/** Shared height of a single grading-group line (keep in sync with StackItem & CellFlex). */
export const GRADING_LINE_HEIGHT = 40;
export const BASE_ROW_HEIGHT = 40;

export const PageWrapper = styled.div`
  display: flex;
  flex-direction: column;
  height: calc(100vh - 48px);
  min-height: 480px;

  /* Push button is disabled until a copy-source row is chosen. */
  .floting-actions .btn:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
`;

export const TitleBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 48px;
  padding: 0 16px;
  background: var(--titleBG, #f3f3f3);
  border-top: 1px solid #d8d8d8;
  border-bottom: 1px solid #d8d8d8;
`;

export const Title = styled.h1`
  margin: 0;
  font-family: var(--font-medium), 'Aktiv Grotesk', system-ui, sans-serif;
  font-size: 24px;
  letter-spacing: 0.24px;
  color: #3c3c3c;
`;

export const TitleGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`;

export const TitleActions = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;

  /* Figma Back button uses #444 text. Scoped override only — the shared master-design
     .btn-light class (color: $color-text) is left untouched. */
  .bombiix-btn.btn-light {
    color: #444;
  }
`;

/** Base look for the grading-push modal's action buttons (see GradingPushModal.styled). */
export const SubmitButton = styled.button`
  height: 32px;
  min-width: 90px;
  border: none;
  border-radius: var(--border-radius-common, 2px);
  background: var(--bombiix-blue, #0c97ee);
  color: #fff;
  font-family: var(--font-medium), 'Aktiv Grotesk', system-ui, sans-serif;
  font-size: 14px;
  cursor: pointer;
  &:hover {
    filter: brightness(0.95);
  }
`;

export const BackButton = styled.button`
  height: 32px;
  min-width: 80px;
  border: 1px solid var(--bordercolor, #6a6a6a);
  border-radius: var(--border-radius-common, 2px);
  background: #fff;
  color: #7f7f7f;
  font-family: var(--font-medium), 'Aktiv Grotesk', system-ui, sans-serif;
  font-size: 14px;
  cursor: pointer;
  &:hover {
    background: #cecece;
  }
`;

/** Fixed-width wrapper for the supplier react-select in the title bar. */
export const FilterField = styled.div`
  width: 200px;
  flex: 0 0 auto;
`;

/**
 * Wraps CommonAgGrid and injects the coloured group-header accents + top-aligned
 * grading cells. These selectors cascade into the grid DOM rendered by the child.
 */
export const GridContainer = styled.div<{ $rowsDisabled?: boolean }>`
  flex: 1;
  min-height: 0;
  padding: 16px 16px 16px;
  display: flex;
  flex-direction: column;

  /* While the page-level Supplier hasn't been picked, lock row editing. Header stays interactive. */
  ${({ $rowsDisabled }) =>
    $rowsDisabled
      ? `
    .ag-body-viewport,
    .ag-pinned-left-cols-container,
    .ag-pinned-right-cols-container {
      pointer-events: none;
      opacity: 0.55;
    }
  `
      : ''}

  .ag-header-group-cell.mssg-grp {
    padding: 0 10px;
  }
  /* Two accent bars per group — one on each edge, in the group's colour (Figma "Bombiix 2026":
     each group is flanked by its own bar, so a boundary shows two colours, e.g. blue│green at
     Costing/Technical Package). Both bars are painted from ONE full-width ::after using two
     hard-stop gradients: the inner content wrapper is position:relative z-index:1 and the resize
     handle is z-index:2, so a per-side ::before at z-index:1 gets buried — this overlay sits at
     z-index:3 (pointer-events:none, transparent except the 3px edges) so both bars always show. */
  .ag-header-group-cell.mssg-grp::after {
    content: '';
    position: absolute;
    left: 0;
    right: 0;
    top: 50%;
    transform: translateY(-50%);
    height: 28px;
    pointer-events: none;
    z-index: 3;
    background-image: linear-gradient(var(--mssg-bar, transparent), var(--mssg-bar, transparent)),
      linear-gradient(var(--mssg-bar, transparent), var(--mssg-bar, transparent));
    background-repeat: no-repeat, no-repeat;
    background-size: 3px 100%, 3px 100%;
    /* Right bar overhangs 1px past the cell edge so it always overlaps the next group's
       left bar — at fractional (e.g. 90%) zoom the two cell edges can round apart and leave
       a 1px gap otherwise. The overlap is hidden under the next cell's bar, so both colours
       still read as a clean two-colour pair. */
    background-position: left top, right -1px top;
  }
  .ag-header-group-cell.mssg-grp--cw {
    --mssg-bar: #669fa6;
  }
  .ag-header-group-cell.mssg-grp--cp {
    --mssg-bar: #6dc1f5;
  }
  .ag-header-group-cell.mssg-grp--tp {
    --mssg-bar: #73c5af;
  }
  .ag-header-group-cell.mssg-grp--sd {
    --mssg-bar: #fdd499;
  }
  .ag-header-group-cell.mssg-grp--rs {
    --mssg-bar: #fa979b;
  }
  .ag-header-group-cell.mssg-grp--ci {
    --mssg-bar: #cbe6a0;
  }
  .ag-header-group-cell.mssg-grp--core {
    --mssg-bar: #f6c26b;
  }
  .ag-header-group-cell.mssg-grp--email {
    --mssg-bar: #d1aaff;
  }

  /* Wrap the header row with top/left/right borders and 4px rounded top corners.
     overflow: hidden clips the inner header cells so their square backgrounds
     don't poke past the rounded corners. */
  .ag-header {
    border-top: 1px solid #d8d8d8;
    border-left: 1px solid #d8d8d8;
    border-right: 1px solid #d8d8d8;
    border-radius: 4px 4px 0 0;
    overflow: hidden;
  }

  /* Top-align cell content so every cell's first line sits level at the top of the
     row. On a single-line (40px) row this reads the same as centred; once a UOM line
     expands the row, the fixed-height CellFlex cells stay pinned to the top instead of
     floating to the vertical middle. Grading cells fill the full (tall) row via a
     100%-height Stack whose lines flow from the top, so they're unaffected either way. */
  .ag-cell {
    display: flex;
    align-items: flex-start;
    padding: 0 10px;
    line-height: 1.4;
    font-size: 14px;
    border: none;
  }

  /* Header carries 1px left+right borders (see .ag-header rule above) but the body
     doesn't, so every body row was shifted 1px left of the header content and the
     right edge fell short too. Add matching 1px transparent borders on .ag-body
     so its inner width lines up with the header. */
  .ag-body.ag-layout-normal {
    border-left: 1px solid transparent;
    border-right: 1px solid transparent;
  }

  /* AG Grid renders an empty .ag-floating-top row band (for pinned top rows / grand
     totals) even when we have none — it just adds .ag-invisible + visibility:hidden.
     The element still takes ~1px of layout height, producing a visible gap between
     the header and the first body row. Collapse it fully when there's nothing pinned. */
  .ag-floating-top.ag-invisible {
    display: none;
  }

  /* Neutralise the stray app icon-font glyph on AG Grid icons — clear the pseudo-
     element content and re-centre it so any inherited ::before rule doesn't stack a
     second chevron next to the built-in one (visible on the Pin Columns submenu arrow). */
  .ag-icon::before {
    content: '';
    font-family: inherit;
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
  }

  /* Row hover — match the master-design BombiixAGGrid token (#e6f5ff). AG Grid v33+
     paints its own hover overlay behind cells; painting directly on the row is enough
     because our cells are transparent by default (only expanded rows have a gradient). */
  .ag-row.ag-row-hover {
    background-color: #e6f5ff;
  }

  .ag-cell[col-id='product-number'] {
    padding: 0 10px;
  }
  .ag-cell.mssg-cell-top {
    padding-top: 0;
    padding-bottom: 0;
  }

  /* Multiselect cells: let the dropdown menu escape the cell. */
  .ag-cell.mssg-ms-cell {
    overflow: visible;
  }
  /* react-multi-select-component renders the menu inline, so cell-level overflow isn't
     enough — it's still clipped by AG Grid ancestor containers. While a menu is open,
     relax overflow on those ancestors so it can escape the grid. Only in the AUTO-HEIGHT
     layout (small row counts); in NORMAL layout .ag-body-viewport IS the vertical scroll
     container so making it visible would reset scrollTop. :has()-scoped so it only
     applies while a menu is open. */
  .ag-root-wrapper.ag-layout-auto-height:has(.rmsc .dropdown-content),
  .ag-root.ag-layout-auto-height:has(.rmsc .dropdown-content),
  .ag-body-viewport.ag-layout-auto-height:has(.rmsc .dropdown-content),
  .ag-layout-auto-height .ag-center-cols-viewport:has(.rmsc .dropdown-content) {
    overflow: visible;
  }
  /* Bottom-of-list rows in the scrolling layout: FlipMultiSelect flags .mssg-ms-flip-up so
     the menu opens upward instead of downward past the scroll viewport. */
  .mssg-ms-flip-up .rmsc .dropdown-content {
    top: auto;
    bottom: calc(100% + 4px);
  }
  /* Single-select (react-select) cells: while a menu is open, give the cell the same escape
     treatment so the inline menu isn't clipped and scrolls with the cell (not portaled). */
  .ag-cell:has(.mssg-rs__menu) {
    overflow: visible;
    z-index: 6;
  }
  /* Lift the whole row that has an open multiselect above the rows below it. */
  .ag-row:focus-within {
    z-index: 5;
  }

  /* rmsc trigger styled to mirror the react-select master design (CellReactSelect):
     28px tall, 4px radius, #d8d8d8 border, Aktiv Grotesk 14px, comma-summary label. */
  .mssg-ms-cell .rmsc {
    --rmsc-h: 28px;
    --rmsc-radius: 4px;
    --rmsc-border: #d8d8d8;
    --rmsc-bg: #fff;
    border: none;
    box-shadow: none;
  }
  .mssg-ms-cell .rmsc .dropdown-container {
    height: 28px;
    min-height: 28px;
    border-radius: 4px;
    border: 1px solid #d8d8d8;
    box-shadow: none;
    background: #fff;
    transition: border-color 0.15s ease;
  }
  .mssg-ms-cell .rmsc .dropdown-container:hover,
  .mssg-ms-cell .rmsc .dropdown-container:focus-within {
    border-color: #d8d8d8;
    box-shadow: none;
  }
  .mssg-ms-cell .rmsc .dropdown-heading {
    height: 26px;
    padding: 0 8px;
    cursor: pointer;
    font-family: 'Aktiv Grotesk', sans-serif;
  }
  .mssg-ms-cell .rmsc .dropdown-heading-value {
    font-size: 14px;
    color: #3c3c3c;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .mssg-ms-cell .rmsc .dropdown-heading-value .gray {
    color: #a4a4a4;
  }
  /* Utility.css sets .rmsc .dropdown-heading-value > span.gray { color: var(--placeholderColor) !important }
     which wins on specificity everywhere else. Add .mssg-ms-ph on the rmsc element (via the
     FlipMultiSelect className prop) to force the master-design placeholder colour #a4a4a4. */
  .rmsc.mssg-ms-ph .dropdown-heading .dropdown-heading-value > span.gray {
    color: #a4a4a4 !important;
  }
  /* Arrow chevron is now rendered by FlipMultiSelect's BombiixArrow (ArrowRenderer prop)
     — matches CellReactSelect / NewSelectMultiSelect. Just handle hover darkening here. */
  .mssg-ms-cell .rmsc:hover .bombiix-icon-chevron-down {
    color: #3c3c3c !important;
  }
  .mssg-ms-cell .rmsc .clear-selected-button {
    padding: 0 4px;
    color: #a4a4a4;
  }

  /* rmsc menu styled to mirror the react-select master design:
     rounded 8px, subtle shadow, 6px padding, 4px gap between options. */
  .mssg-ms-cell .rmsc .dropdown-content {
    z-index: 30;
    padding-top: 4px;
    min-width: 200px;
    width: max-content;
    max-width: 260px;
  }
  /* Narrow variant for compact cells (e.g. the Size grading column at 110px). Add
     .mssg-ms-cell--narrow alongside .mssg-ms-cell on the AG Grid cell class. */
  .mssg-ms-cell--narrow .rmsc .dropdown-content {
    min-width: 140px;
    max-width: 160px;
  }
  .mssg-ms-cell .rmsc .dropdown-content .panel-content {
    border-radius: 8px;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.25);
    border: none;
    overflow: hidden;
  }
  .mssg-ms-cell .rmsc .dropdown-content .options {
    padding: 6px;
    display: flex;
    flex-direction: column;
    gap: 4px;
  }
  .mssg-ms-cell .rmsc .dropdown-content .options li {
    margin: 0;
  }
  /* Option row (Figma node 4841:128341) — 20px min-height, 6px h-padding, 8px gap,
     4px radius, hover / selected #ceeafc, Aktiv Grotesk 12px #3c3c3c label. */
  .mssg-ms-cell .rmsc .dropdown-content .select-item {
    min-height: 20px;
    padding: 2px 6px;
    border-radius: 4px;
    cursor: pointer;
  }
  .mssg-ms-cell .rmsc .dropdown-content .select-item:hover,
  .mssg-ms-cell .rmsc .dropdown-content .select-item.selected {
    background: #ceeafc;
  }
  /* FlipMultiSelect ships its own ItemRenderer that renders the Bombiix
     master-design 16px checkbox (.bombiix-checkbox--sm). The wrapper cell
     still carries the app-wide .common-new-multiselect class, which paints a
     fake ::before checkbox on every <span> inside .item-renderer — those
     overrides must be cleared here so only the master-design box remains. */
  .mssg-ms-cell .rmsc .dropdown-content .item-renderer {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 0;
  }
  .mssg-ms-cell .rmsc .dropdown-content .item-renderer span::before {
    content: none;
    display: none;
    background: none;
    width: 0;
    height: 0;
    margin: 0;
  }
  .mssg-ms-cell .rmsc .dropdown-content .item-renderer .mssg-ms-item-label {
    flex: 1;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    font-family: 'Aktiv Grotesk', sans-serif;
    font-size: 12px;
    font-weight: 500;
    line-height: 16px;
    color: #3c3c3c;
    padding-top: 2px;
  }

  /* Remove the table's outer left & right border lines, plus the square top
     border so it doesn't cut across the header's rounded top corners. */
  .ag-root-wrapper {
    border-left: none;
    border-right: none;
    border-top: none;
  }

  /* Copy-source row: light tint (Figma #6bbbfa) on the row and its cells, pinned columns included. */
  .ag-row.mssg-row-source,
  .ag-row.mssg-row-source .ag-cell {
    background-color: rgba(107, 187, 250, 0.1);
  }

  /* Expanded row: parent (first BASE_ROW_HEIGHT slice) stays white, everything below —
     the child grading lines area — gets the light-blue fill (Figma #ECF8FF). Painted as a
     linear gradient on the row and each cell's background is nulled so the row gradient
     shows through. */
  .ag-row.mssg-row-expanded {
    background: linear-gradient(to bottom, #fff 0, #fff ${BASE_ROW_HEIGHT}px, #ecf8ff ${BASE_ROW_HEIGHT}px, #ecf8ff 100%);
  }
  .ag-row.mssg-row-expanded .ag-cell {
    background-color: transparent;
  }

  /* NewDatePicker trigger text — align with the rest of the multi-submit cells
     (react-select + inputs all use Aktiv Grotesk). The master datepicker scss doesn't
     set a font-family on .bombiix-date-trigger__text, so it inherits the page font;
     force Aktiv Grotesk here for consistency with sibling cells. */
  .bombiix-date-trigger__text {
    font-family: 'Aktiv Grotesk', sans-serif;
  }

  /* Keep the Due Date trigger border neutral in every state — hover / open /
     focus-visible normally recolour to placeholder-grey or blue, but Multi Submit
     cells stay flat like the sibling react-select controls. */
  .bombiix-date-trigger:hover:not(:disabled),
  .bombiix-date-trigger--open,
  .bombiix-date-trigger:focus-visible:not(:disabled) {
    border-color: #d8d8d8;
    box-shadow: none;
  }

  /* Placeholder text in the @-mention recipient inputs (To/Cc/Bcc) at 14px. */
  .ms-mention textarea::placeholder,
  .ms-mention input::placeholder {
    font-size: 14px;
  }

  /* ── Column menu (Product Number three-dot) — Figma node 5124:195038 ──
     AG Grid renders menu popups inside .ag-root-wrapper (default popupParent),
     so these rules reach the menu yet stay local to this grid. */
  .ag-menu {
    border: none;
    border-radius: 8px;
    box-shadow: 0 2px 2px rgba(0, 0, 0, 0.25);
    background: #fff;
    /* Figma popup is a fixed 204px card (192px rows + 6px padding). */
    min-width: 204px;
  }
  .ag-menu .ag-menu-list {
    padding: 6px;
    box-sizing: border-box;
  }
  .ag-menu .ag-menu-option {
    border-radius: 4px;
  }
  .ag-menu .ag-menu-option-active {
    background-color: #ceeafc;
  }
  .ag-menu .ag-menu-option-part {
    padding-top: 2px;
    padding-bottom: 2px;
  }
  /* Text parts at 12px medium #3c3c3c; icon parts keep their native glyph size. */
  .ag-menu .ag-menu-option-text,
  .ag-menu .ag-menu-option-shortcut {
    font-family: var(--font-medium), 'Aktiv Grotesk', system-ui, sans-serif;
    font-size: 12px;
    line-height: 19px;
    color: #3c3c3c;
  }
  .ag-menu .ag-menu-option-icon {
    padding-left: 4px;
    padding-right: 8px;
    color: #3c3c3c;
  }
  .ag-menu .ag-menu-option-icon svg {
    display: block;
    width: 16px;
    height: 16px;
  }
  .ag-menu .ag-menu-option-text {
    padding-right: 8px;
  }
  /* Submenu pointer (Pin Columns ›) tinted to match the Figma menu text. */
  .ag-menu .ag-menu-option-popup-pointer {
    color: #3c3c3c;
  }
  .ag-menu .ag-menu-separator-part::after {
    border-top: 1px solid #d8d8d8;
  }
`;

/** Scrollable grid area that flexes to fill the container, leaving room for the load-more bar. */
export const GridScrollArea = styled.div`
  flex: 1;
  min-height: 0;
  overflow: auto;
`;

/** Footer strip shown while the next infinite-scroll chunk's data is loading. */
export const LoadMoreBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 32px;
  flex: 0 0 auto;
  font-size: 13px;
  color: var(--placeholderColor, #6a6a6a);
`;
